// Thin JSON client for /api/v1/* with timeout. Same-origin (Pages serves both static + functions).

export async function apiGet<T>(path: string, timeoutMs = 10_000): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(path, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

// ---- Response types (mirror phase 3 contracts) ----

export interface LatestMid {
  series: string;
  kind: "mid";
  price: number;
  currency: string;
  ts: number;
  delta24h: Delta | null;
}
export interface LatestBuySell {
  series: string;
  kind: "buysell";
  buy: number;
  sell: number;
  currency: string;
  ts: number;
  delta24h: Delta | null;
}
export type LatestEntry = LatestMid | LatestBuySell;
export interface Delta {
  abs: number;
  pct: number;
  basis: string;
}

export interface SeriesPoint {
  ts: number;
  price: number | null;
  buy: number | null;
  sell: number | null;
}

export interface Brief {
  id: string;
  date: string;
  topic: string;
  brief_vi: string;
  outlook: "up" | "down" | "sideways" | null;
  confidence: number | null;
  source_urls: string; // JSON array
  model: string | null;
  created_at: number;
}

export interface NewsItem {
  id: string;
  title: string;
  summary: string | null;
  url: string;
  topics: string | null;
  province_code: string | null;
  scope: string;
  source: string | null;
  published_at: number;
}

export interface Health {
  sources: Array<{ source: string; fetched_at: number | null; ok: boolean; stale: boolean }>;
  news: { last_ingested_at: number | null; stale: boolean };
  briefs: { last_created_at: number | null; stale: boolean };
}

export const getLatest = () => apiGet<{ series: LatestEntry[] }>("/api/v1/prices/latest");
export const getSeries = (series: string, range: string) =>
  apiGet<{ series: string; range: string; points: SeriesPoint[] }>(
    `/api/v1/prices/series?series=${series}&range=${range}`,
  );
export const getBriefs = () => apiGet<{ briefs: Brief[] }>("/api/v1/briefs");
export const getNews = (params = "") => apiGet<{ items: NewsItem[] }>(`/api/v1/news${params}`);
export const getHealth = () => apiGet<Health>("/api/v1/health");
export const getNewsDensity = (days: 7 | 30) =>
  apiGet<{ days: number; density: Array<{ province_code: string; count: number }> }>(
    `/api/v1/provinces/news-density?days=${days}`,
  );

// --- Intelligence platform (events / indicators / insights) ---

export interface EventItem {
  id: string;
  news_id: string;
  event_type: string;
  province_code: string | null;
  occurred_at: string | null;
  severity: number;
  metrics: Record<string, number> | null;
  title_vi: string;
  summary_vi: string | null;
  confidence: number;
  extractor_version: string;
  news_url: string | null;
  news_title: string | null;
}

export interface WeeklyIndicator {
  province_code: string;
  domain: string;
  week: string;
  event_count: number;
  severity_sum: number;
  sentiment_avg: number | null;
  news_count: number;
}

export interface Insight {
  id: string;
  week: string;
  scope: string;
  insight_type: string;
  domain: string | null;
  title: string;
  body_vi: string;
  evidence: string[] | null;
  confidence: number | null;
  created_at: number;
}

export const getEvents = (params = "") =>
  apiGet<{ items: EventItem[] }>(`/api/v1/events${params}`);
export const getIndicatorsWeekly = (params = "") =>
  apiGet<{ items: WeeklyIndicator[] }>(`/api/v1/indicators/weekly${params}`);
export const getInsights = (params = "") =>
  apiGet<{ items: Insight[] }>(`/api/v1/insights${params}`);

export interface MacroSeriesPoint {
  period: string;
  value: number;
  meta: { sample_n?: number; p25?: number; p75?: number } | null;
}
export interface ProvinceIndicator {
  province_code: string;
  period: string;
  value: number;
  prev_value: number | null;
  prev_period: string | null;
  meta: { sample_n?: number; p25?: number; p75?: number } | null;
}
export const getMacroSeries = (params = "") =>
  apiGet<{ indicator: string; province: string | null; points: MacroSeriesPoint[] }>(
    `/api/v1/macro/series${params}`,
  );
export const getMacroByProvince = (indicator: string) =>
  apiGet<{ indicator: string; items: ProvinceIndicator[] }>(
    `/api/v1/macro/by-province?indicator=${encodeURIComponent(indicator)}`,
  );

// Composite economic-health index (experimental). score 0..100 (50 neutral);
// active_count of total_components (8) says how many had enough data.
export interface HealthComponent {
  key: string;
  label: string;
  z: number | null;
  direction: 1 | -1 | null;
  contribution: number | null;
}
// Discriminated union on `available` so the empty shape can't be read as if it had a
// score — the compiler forces a guard before touching score/components.
export interface HealthIndexUnavailable {
  available: false;
  total_components: number;
}
export interface HealthIndexAvailable {
  available: true;
  total_components: number;
  ts: number;
  score: number;
  band_low: number | null;
  band_high: number | null;
  active_count: number;
  components: HealthComponent[];
  history: { ts: number; score: number; band_low: number | null; band_high: number | null }[];
}
export type HealthIndex = HealthIndexUnavailable | HealthIndexAvailable;
export const getHealthIndex = () => apiGet<HealthIndex>("/api/health-index");
